import React, {
  useEffect, useMemo, useReducer,
} from 'react';
import {
  Input, InputGroup, Panel, Stack, Table, CheckPicker,
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import { PartnerEshopSalesEntity } from '../api/apiTypes';
import { getAllPartnerEshopSalesEntities } from '../api/salesentity/salesEntity';
import ApiGroupIcon from '../utils/apiGroup';
import { utils } from '../utils/utils';
import { PaginationScreenKey } from '../services/paginationLimitService';
import usePaginationLimit from '../hooks/paginationLimitHook';
import Pagination from '../components/Pagination';

const FILTER_KEYS: (keyof PartnerEshopSalesEntity)[] = [
  'salesOrganizationName',
  'partnerCode',
  'partnerName',
];

function filterBySelectedPickerValues(
  data: PartnerEshopSalesEntity[],
  selectedSalesEntityCodes: string[],
  selectedPartnerNames: string[],
  selectedApiGroups: string[],
  selectedMarketplaceCodes: string[],
): PartnerEshopSalesEntity[] {
  return data.filter((item) => {
    const matchesSalesEntity = selectedSalesEntityCodes.length === 0
        || selectedSalesEntityCodes.includes(item.salesOrganizationCode);
    const matchesPartner = selectedPartnerNames.length === 0
        || selectedPartnerNames.includes(item.partnerName);
    const matchesApiGroup = selectedApiGroups.length === 0
        || selectedApiGroups.includes(item.apiGroup);
    const matchesMarketplace = selectedMarketplaceCodes.length === 0
        || selectedMarketplaceCodes.includes(item.marketplaceCode);

    return matchesSalesEntity
        && matchesPartner
        && matchesApiGroup
        && matchesMarketplace;
  });
}

interface State {
  loading: boolean;
  eshopsSalesEntities: PartnerEshopSalesEntity[];
  searchFilter: string;
  selectedSalesEntityCodes: string[];
  selectedPartnerNames: string[];
  selectedMarketplaceCodes: string[];
  selectedApiGroups: string[];
  page: number;
}

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DATA'; payload: PartnerEshopSalesEntity[] }
  | { type: 'SET_SEARCH_FILTER'; payload: string }
  | { type: 'SET_SALES_ENTITY_CODES'; payload: string[] }
  | { type: 'SET_PARTNER_NAMES'; payload: string[] }
  | { type: 'SET_MARKETPLACE_CODES'; payload: string[] }
  | { type: 'SET_API_GROUPS'; payload: string[] }
  | { type: 'SET_PAGE'; payload: number };

const initialState: State = {
  loading: true,
  eshopsSalesEntities: [],
  searchFilter: '',
  selectedSalesEntityCodes: [],
  selectedPartnerNames: [],
  selectedMarketplaceCodes: [],
  selectedApiGroups: [],
  page: 1,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_DATA':
      return { ...state, eshopsSalesEntities: action.payload };
    case 'SET_SEARCH_FILTER':
      return { ...state, searchFilter: action.payload, page: 1 };
    case 'SET_SALES_ENTITY_CODES':
      return { ...state, selectedSalesEntityCodes: action.payload, page: 1 };
    case 'SET_PARTNER_NAMES':
      return { ...state, selectedPartnerNames: action.payload, page: 1 };
    case 'SET_MARKETPLACE_CODES':
      return { ...state, selectedMarketplaceCodes: action.payload, page: 1 };
    case 'SET_API_GROUPS':
      return { ...state, selectedApiGroups: action.payload, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    default:
      return state;
  }
}

export default function PartnersSalesEntities() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [limit, setLimit] = usePaginationLimit(PaginationScreenKey.PARTNER_SALES_ENTITIES);

  const {
    loading,
    eshopsSalesEntities,
    searchFilter,
    selectedSalesEntityCodes,
    selectedPartnerNames,
    selectedMarketplaceCodes,
    selectedApiGroups,
    page,
  } = state;

  useEffect(() => {
    getAllPartnerEshopSalesEntities()
      .then((result) => {
        dispatch({ type: 'SET_DATA', payload: result });
      })
      .finally(() => {
        dispatch({ type: 'SET_LOADING', payload: false });
      });
  }, []);

  const uniqueValues = useMemo(() => {
    const salesEntities = new Set<string>();
    const partnerNames = new Set<string>();
    const marketplaceCodes = new Set<string>();
    const apiGroups = new Set<string>();

    eshopsSalesEntities.forEach((item) => {
      salesEntities.add(item.salesOrganizationCode);
      partnerNames.add(item.partnerName);
      marketplaceCodes.add(item.marketplaceCode);
      apiGroups.add(item.apiGroup);
    });

    const mapValuesToPicker = (values: Set<string>) => Array
      .from(values)
      .sort()
      .map(value => ({ label: value, value }));

    return {
      salesEntities: mapValuesToPicker(salesEntities),
      partnerNames: mapValuesToPicker(partnerNames),
      marketplaceCodes: mapValuesToPicker(marketplaceCodes),
      apiGroups: mapValuesToPicker(apiGroups),
    };
  }, [eshopsSalesEntities]);

  const filteredData = useMemo(() => {
    const fulltextSearchFiltered = utils.fulltextFiltered(
      eshopsSalesEntities,
      searchFilter,
      FILTER_KEYS,
    );

    return filterBySelectedPickerValues(
      fulltextSearchFiltered,
      selectedSalesEntityCodes,
      selectedPartnerNames,
      selectedApiGroups,
      selectedMarketplaceCodes,
    );
  }, [
    eshopsSalesEntities,
    searchFilter,
    selectedSalesEntityCodes,
    selectedPartnerNames,
    selectedMarketplaceCodes,
    selectedApiGroups,
  ]);

  const paginatedData = useMemo(
    () => utils.paginated(filteredData, { page, limit }),
    [filteredData, page, limit],
  );

  function handleChangePage(value: number): void {
    dispatch({ type: 'SET_PAGE', payload: value });
  }

  function handleChangeLimit(value: number): void {
    setLimit(value);
    handleChangePage(1);
  }

  return (
    <Panel header={<h3>Partners’ entities</h3>}>
      <Stack direction="column" alignItems="stretch" spacing={10}>
        <Stack direction="row" spacing={10} wrap>
          <InputGroup inside style={{ width: 400 }}>
            <Input
              placeholder="search by entity name, partner code, partner name"
              value={searchFilter}
              onChange={(value) => dispatch({ type: 'SET_SEARCH_FILTER', payload: value })}
            />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>

          <CheckPicker
            data={uniqueValues.salesEntities}
            placeholder="Business entity"
            value={selectedSalesEntityCodes}
            onChange={(value) => dispatch({ type: 'SET_SALES_ENTITY_CODES', payload: value })}
            style={{ width: 200 }}
          />
          <CheckPicker
            data={uniqueValues.partnerNames}
            placeholder="Partner"
            value={selectedPartnerNames}
            onChange={(value) => dispatch({ type: 'SET_PARTNER_NAMES', payload: value })}
            style={{ width: 200 }}
          />
          <CheckPicker
            data={uniqueValues.marketplaceCodes}
            placeholder="Marketplace"
            value={selectedMarketplaceCodes}
            onChange={(value) => dispatch({ type: 'SET_MARKETPLACE_CODES', payload: value })}
            style={{ width: 200 }}
          />
          <CheckPicker
            data={uniqueValues.apiGroups}
            placeholder="API Group"
            value={selectedApiGroups}
            onChange={(value) => dispatch({ type: 'SET_API_GROUPS', payload: value })}
            style={{ width: 200 }}
          />
        </Stack>

        <Table
          autoHeight
          data={paginatedData}
          loading={loading}
        >
          <Table.Column width={250}>
            <Table.HeaderCell>Business entity name</Table.HeaderCell>
            <Table.Cell dataKey="salesOrganizationName" />
          </Table.Column>

          <Table.Column width={200}>
            <Table.HeaderCell>Partner code</Table.HeaderCell>
            <Table.Cell dataKey="partnerCode" />
          </Table.Column>

          <Table.Column width={400}>
            <Table.HeaderCell>Partner name</Table.HeaderCell>
            <Table.Cell dataKey="partnerName" />
          </Table.Column>

          <Table.Column width={200}>
            <Table.HeaderCell>Marketplace code</Table.HeaderCell>
            <Table.Cell dataKey="marketplaceCode" />
          </Table.Column>

          <Table.Column width={120} align="center">
            <Table.HeaderCell>API Group</Table.HeaderCell>
            <Table.Cell>
              {(rowData: PartnerEshopSalesEntity) => (
                <ApiGroupIcon apiGroup={rowData.apiGroup} />
              )}
            </Table.Cell>
          </Table.Column>
        </Table>

        <div style={{ padding: 20 }}>
          <Pagination
            total={filteredData.length}
            limit={limit}
            activePage={page}
            onChangePage={(value) => handleChangePage(value)}
            onChangeLimit={(value) => handleChangeLimit(value)}
          />
        </div>
      </Stack>
    </Panel>
  );
}
